import React, { createElement, forwardRef, useRef } from "react";

import { InView } from "react-intersection-observer";

import { useHomePageId } from "@/home/useHomePage/useHomePage";
import useGetUser from "@/users/v3/useGetUser";
import useTracker from "../useTracker";
import propTypes from "./withTrackEvent.constants";

const defaultProps = {
  childrenWrapperElement: "div",
  trackClick: true,
  trackHover: false,
  trackImpression: false,
  trackView: false,
};

/**
 * withTrackEvent is a higher order component (HOC) that facilitates the event
 * tracking of the new 'event-stream' kinesis streams.
 *
 * SIDE EFFECTS: This HOC wraps passed children with a div, or the HTML tag defined
 * in childrenWrapperElement prop. This might cause layout side effects for styling.
 * You might need to take into account the wrapping element.
 *
 * IMPORTANT: Components that are wrapped with this HOC will have a prop called `ref`
 * that needs to be given to a DOM element in order for the tracking to work.
 * This can be done in two ways depending on the component you're wrapping:
 *   1. Take the `props.ref` value and put it onto a DOM element in your component.
 *
 *   For example, if your wrapped component returns HTML, you can do...
 *
 *   return <div ref={props.ref}>Hello</div>
 *
 *   2. If your component doesn't return a DOM element, you'll need to make your component
 *   a .forwardRef() and pass the ref down to a component that does render a DOM element.
 *   See https://reactjs.org/docs/forwarding-refs.html for more info.
 *
 * @param  {ReactComponent} WrappedComponent
 */

export const composeEventData = (
  props,
  getEventNameForEventStream,
  homePageId,
  userRoles,
) => {
  const userRolesString = Array.isArray(userRoles) ? userRoles.join(",") : null;
  const eventData = {
    event_name: getEventNameForEventStream(
      props.objectType,
      props.actionPrefix,
      props.actionName,
    ),
    dimension_properties: {
      homepage_id: homePageId,
      user_roles: userRolesString,
      ...props.streamProperties,
    },
  };

  return eventData;
};

const withTrackEvent = (WrappedComponent) => {
  const WithTrackEvent = (props) => {
    const { trackAction, getEventNameForEventStream } = useTracker();
    const homePageId = useHomePageId();
    const { data: user } = useGetUser();
    const hoverStartTime = useRef(0);

    // Handles click events
    const handleClick = (e) => {
      trackAction({
        ...composeEventData(
          props,
          getEventNameForEventStream,
          homePageId,
          user?.roles,
        ),
        event_type: "click",
      });

      if (props.onClick) {
        props.onClick(e);
      }
    };

    // Handles hover events
    const handleHoverStart = (e) => {
      hoverStartTime.current = Date.now();
      if (props.onMouseEnter) {
        props.onMouseEnter(e);
      }
    };

    const handleHoverEnd = (e) => {
      const stopTime = Date.now();
      const delta = stopTime - hoverStartTime.current;
      if (props.onMouseLeave) {
        props.onMouseLeave(e);
      }
      trackAction({
        event_type: "hover",
        hoverTime: `${delta}ms`,
        ...composeEventData(
          props,
          getEventNameForEventStream,
          homePageId,
          user?.roles,
        ),
      });
    };

    // Handles Impression events
    const handleImpression = (inView) => {
      if (!inView) return;
      const composeImpression = () => {
        // Grab href located inside each content card
        // Invoke track action
        return trackAction({
          event_type: "impression",
          ...composeEventData(
            props,
            getEventNameForEventStream,
            homePageId,
            user?.roles,
          ),
        });
      };

      composeImpression();
    };

    const trackingProps = {
      ...props,
      onClick: handleClick,
    };

    // The `data-tracker-*` attributes were added for Heap autocapture, whose
    // events also carried some of the surrounding HTML. Heap is gone, but
    // these attributes stay for now: snitch's own `withTrackEvent` emits the
    // identical pair, and Lyceum has a live layout rule keyed on
    // `[data-tracker-model]` (see `QuickAssignmentForm.style.ts`) plus a few
    // components that set it by hand. Removing them is a rendered-DOM change
    // across the whole app and needs its own ticket. Nothing new should start
    // relying on `data-tracker-*` in the meantime. If you find something else
    // relying on it, please inform the Front End Platform team.
    const dataTrackerProps = {
      "data-tracker-format": props.streamProperties?.content_type,
      "data-tracker-model": props.objectType,
    };

    if (props.trackHover) {
      trackingProps.onMouseEnter = handleHoverStart;
      trackingProps.onMouseLeave = handleHoverEnd;
    }

    if (props.trackImpression) {
      return (
        <InView
          threshold={0.75}
          as={props.childrenWrapperElement}
          onChange={handleImpression}
          {...dataTrackerProps}
        >
          <WrappedComponent {...trackingProps} ref={props.forwardedRef} />
        </InView>
      );
    }
    // If not intented to track impression, avoid wrap with InView and attach
    // a scrolling event listener, instead wrap with childrenWrapperElement
    // we need createElement because it can use any valid html tag to wrap it.
    return createElement(
      props.childrenWrapperElement || "div",
      { ...dataTrackerProps },
      <WrappedComponent {...trackingProps} ref={props.forwardedRef} />,
    );
  };

  WithTrackEvent.propTypes = propTypes;

  return forwardRef((props, ref) => (
    <WithTrackEvent {...props} forwardedRef={ref} />
  ));
};

withTrackEvent.defaultProps = defaultProps;
withTrackEvent.propTypes = propTypes;

export default withTrackEvent;
